import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DecimalPipe, NgClass } from '@angular/common';
import { Reservation } from '../../../../services/reservation';
import { FormField, FormSelect, FormTextarea } from '@one-of-one/form';
import {
  Pack,
  ParticipantDto,
  Profession,
  RegistrationPass,
  RegistrationType,
} from '@one-of-one/models';
import { PageLayout } from '@one-of-one/form';
import { Router } from '@angular/router';

@Component({
  selector: 'app-simple',
  imports: [
    ReactiveFormsModule,
    FormField,
    FormSelect,
    FormField,
    DecimalPipe,
    PageLayout,
    FormTextarea,
    NgClass,
  ],
  templateUrl: './simple.html',
})
export class Simple implements OnInit {
  private fb = inject(FormBuilder);
  private readonly reservService = inject(Reservation);
  private readonly router = inject(Router);

  isSubmitting = signal(false);

  professions = [
    {
      label: 'Médecin',
      value: Profession.DOCTOR,
    },
    {
      label: 'Non médecin',
      value: Profession.NON_DOCTOR,
    },
    {
      label: 'Résident',
      value: Profession.RESIDENT,
    },
  ];

  passes = [
    {
      id: RegistrationPass.PASS_1,
      period: 'DU 21 AU 23',
    },
    {
      id: RegistrationPass.PASS_2,
      period: 'VENDREDI',
    },
    {
      id: RegistrationPass.PASS_3,
      period: 'SAMEDI',
    },
  ];

  registrationForm = this.fb.group(
    {
      pass: ['', [Validators.required]],
      familyName: ['', [Validators.required, Validators.minLength(2)]],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      profession: this.fb.control('', {
        validators: [Validators.required],
        updateOn: 'change',
      }),
      speciality: ['', [Validators.required]],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern('^[- +()0-9]+$')],
      ],
      email: ['', [Validators.required, Validators.email]],
      city: ['', [Validators.required]],
      country: ['', [Validators.required]],
      comment: ['', [Validators.maxLength(300)]],
    },
    { updateOn: 'submit' },
  );

  totalAmount = signal(0);

  ngOnInit(): void {
    const passCtrl = this.registrationForm.get('pass');

    this.selectRoom(RegistrationPass.PASS_1);

    this.professionChange();
    passCtrl?.valueChanges.subscribe(() => {
      if (passCtrl?.value !== RegistrationPass.PASS_1) {
        this.totalAmount.set(1000);
      } else if (passCtrl?.value === RegistrationPass.PASS_1) {
        this.professionChange();
      }
    });
  }

  private mapFormToParticipant(): ParticipantDto {
    const v = this.registrationForm.value;

    return {
      familyName: v.familyName || '',
      firstName: v.firstName || '',
      profession: v.profession as Profession,
      speciality: v.speciality || '',
      phoneNumber: v.phoneNumber || '',
      email: v.email || '',
      city: v.city || '',
      country: v.country || '',
      pack: Pack.PACK_1,
      roommate: 0,
    };
  }

  onSubmit() {
    if (this.registrationForm.invalid) {
      this.registrationForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    this.reservService
      .createReservation({
        type: RegistrationType.SIMPLE,
        participants: [this.mapFormToParticipant()],
        comment: this.registrationForm.value.comment || '',
        pass: this.registrationForm.value.pass as RegistrationPass,
      })
      .subscribe({
        next: (data) => {
          this.isSubmitting.set(false);
          this.router.navigate(['confirmation', data]);
        },
        error: (error) => {
          this.isSubmitting.set(false);
          this.registrationForm.markAllAsTouched();
          console.error(error);
        },
      });
  }

  selectRoom(type: string) {
    this.registrationForm.get('pass')?.setValue(type);
  }

  professionChange() {
    const professionCtrl = this.registrationForm.get('profession');
    const passCtrl = this.registrationForm.get('pass');

    if (!professionCtrl) return;
    if (!passCtrl) return;

    const getAmount = (
      profession: Profession | string | null | undefined,
    ): number => {
      if (passCtrl.value === RegistrationPass.PASS_1) {
        switch (profession) {
          case Profession.DOCTOR:
            return 2000;
          case Profession.NON_DOCTOR:
          case Profession.RESIDENT:
            return 1500;
          default:
            return 0;
        }
      }
      return 1000;
    };

    // Set initial value
    this.totalAmount.set(getAmount(professionCtrl.value));

    // Subscribe to changes
    professionCtrl.valueChanges.subscribe((value) => {
      this.totalAmount.set(getAmount(value));
    });
  }

  onCancel() {
    this.router.navigate(['/']);
  }
}
